import type { AdminInvestorPersona } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Brain, Play } from 'lucide-react'
import { useState } from 'react'
import { useAdminTriggerPersonaResearchMutation } from '@/hooks/api'

type ResearchVersion = 'monolith-research.v1' | 'decomposed-research.v1'

interface ResearchRequest {
  id: string
  status: 'PENDING' | 'ENQUEUED' | 'ACTIVE' | 'COMPLETED' | 'FAILED' | 'CANCELED'
  version: ResearchVersion
  createdAt: string
  error: string | null
}

const RESEARCH_VERSIONS = [
  { value: 'monolith-research.v1', label: 'Monolith v1' },
  { value: 'decomposed-research.v1', label: 'Decomposed v1' },
] as const satisfies ReadonlyArray<{ value: ResearchVersion; label: string }>

interface AdminPersonaSectionProps {
  investorId: string
  personas: AdminInvestorPersona[] | undefined
  researchRequests: ResearchRequest[] | undefined
  isLoading: boolean
}

const WEIGHT_LABELS: Record<number, string> = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Very High',
}

const METRIC_LABELS: Record<string, string> = {
  team: 'Team',
  market: 'Market',
  problem_solution: 'Problem & Solution',
  traction: 'Traction',
  vision: 'Vision',
}

const STAGE_LABELS: Record<string, string> = {
  pre_seed: 'Pre-Seed',
  seed: 'Seed',
}

function statusVariant(status: string) {
  switch (status) {
    case 'COMPLETED':
      return 'success' as const
    case 'ACTIVE':
    case 'PENDING':
      return 'warning' as const
    case 'FAILED':
    case 'CANCELED':
      return 'destructive' as const
    default:
      return 'secondary' as const
  }
}

export function AdminPersonaSection({
  investorId,
  personas,
  researchRequests,
  isLoading,
}: AdminPersonaSectionProps) {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null)
  const [researchVersion, setResearchVersion] = useState<ResearchVersion>(
    RESEARCH_VERSIONS[0].value
  )

  const triggerMutation = useAdminTriggerPersonaResearchMutation(investorId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  const sortedPersonas = personas ?? []
  const latestResearch = researchRequests?.[0] ?? null

  // Select persona: use selectedVersion if set, otherwise latest (first in desc-sorted list)
  const persona =
    selectedVersion !== null
      ? (sortedPersonas.find((p) => p.version === selectedVersion) ?? null)
      : (sortedPersonas[0] ?? null)

  return (
    <div className="space-y-6">
      {/* Header with research status + trigger */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="size-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Persona</h3>
          {latestResearch && (
            <Badge variant={statusVariant(latestResearch.status)} size="sm">
              {latestResearch.status}
            </Badge>
          )}
          {latestResearch && (
            <span className="text-xs text-muted-foreground">
              {latestResearch.version} &middot;{' '}
              {new Date(latestResearch.createdAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={researchVersion}
            onChange={(e) => setResearchVersion(e.target.value as ResearchVersion)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            {RESEARCH_VERSIONS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={() =>
              triggerMutation.mutate({
                path: { id: investorId },
                body: {
                  version: researchVersion,
                },
              })
            }
            disabled={triggerMutation.isPending}
          >
            <Play className="size-3.5 mr-1" />
            {triggerMutation.isPending ? 'Starting...' : 'Run Research'}
          </Button>
        </div>
      </div>

      {latestResearch?.error && (
        <div className="rounded-lg border border-red-600/30 bg-red-600/10 p-3 text-sm text-red-400">
          {latestResearch.error}
        </div>
      )}

      {/* Version selector */}
      {sortedPersonas.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Version:</span>
          <select
            value={persona?.version ?? ''}
            onChange={(e) => setSelectedVersion(Number(e.target.value))}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            {sortedPersonas.map((p) => (
              <option key={p.id} value={p.version}>
                v{p.version} — {p.source ?? 'unknown'} ({new Date(p.createdAt).toLocaleDateString()}
                )
              </option>
            ))}
          </select>
        </div>
      )}

      {!persona ? (
        <p className="text-sm text-muted-foreground py-4">
          No persona generated yet.
          {latestResearch?.status === 'PENDING' && ' Research is pending...'}
          {latestResearch?.status === 'ACTIVE' && ' Research is in progress...'}
        </p>
      ) : (
        <>
          {/* Brief & Description */}
          <div className="space-y-4">
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Brief</h4>
              <p className="text-sm">{persona.brief}</p>
            </div>

            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
              <p className="text-sm whitespace-pre-wrap">{persona.description}</p>
            </div>

            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Version: {persona.version}</span>
              {persona.source && <span>Source: {persona.source}</span>}
              <span>Created: {new Date(persona.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Metric Prompts */}
          {persona.metricPrompts.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Metric Prompts ({persona.metricPrompts.length})
              </h4>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Metric
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Stage
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Weight
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Prompt
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {persona.metricPrompts.map((mp) => {
                      const isExpanded = expandedPromptId === mp.id
                      return (
                        <tr
                          key={mp.id}
                          className="cursor-pointer hover:bg-muted/30"
                          onClick={() => setExpandedPromptId(isExpanded ? null : mp.id)}
                        >
                          <td className="px-4 py-2 font-medium align-top whitespace-nowrap">
                            {METRIC_LABELS[mp.metric] ?? mp.metric}
                          </td>
                          <td className="px-4 py-2 align-top whitespace-nowrap">
                            <Badge variant="outline" size="sm">
                              {STAGE_LABELS[mp.stage] ?? mp.stage}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 align-top whitespace-nowrap">
                            <Badge variant={mp.weight >= 3 ? 'primary' : 'secondary'} size="sm">
                              {WEIGHT_LABELS[mp.weight] ?? mp.weight}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            <p
                              className={`whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}
                            >
                              {mp.prompt}
                            </p>
                            <span className="text-xs text-muted-foreground/60 mt-1 inline-block">
                              {isExpanded ? 'Click to collapse' : 'Click to expand'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Research requests history */}
      {researchRequests && researchRequests.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Research Requests ({researchRequests.length})
          </h4>
          <div className="space-y-2">
            {researchRequests.map((rr) => (
              <div key={rr.id} className="flex items-center gap-3 text-sm">
                <Badge variant={statusVariant(rr.status)} size="sm">
                  {rr.status}
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">{rr.id}</span>
                <span className="text-muted-foreground">{rr.version}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(rr.createdAt).toLocaleDateString()}
                </span>
                {rr.error && (
                  <span className="text-xs text-red-400 truncate max-w-xs">{rr.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
